/**
 * ObjectReferencePanel React component
 * 
 * @author    Jens Gruschel
 * @copyright © 2019 contexagon GmbH
 */


// React
import React from 'react';
import PropTypes from 'prop-types';
import connect from "react-redux/es/connect/connect";

// Material-UI
import {withStyles} from "@material-ui/core";

// Material-UI components
import CircularProgress from '@material-ui/core/CircularProgress';

// application components
import ObjectTable from "../listing/ObjectTable";
import WarningPanel from "../base/WarningPanel";

// application
import {fetchJSON} from "../../operations/communication";
import {
    REST_CALL_OBJECT_QUERY,
    REST_SERVER_BASE_URL
} from "../../config";
import {
    pushAndEditObject
} from "../../operations/editing";


/**
 * ObjectReferencePanel shows the references of an object
 *
 * @author Jens Gruschel
 */
export class ObjectReferencePanel extends React.Component {

    _isMounted = false;

    state = {
        waiting: false,
        disconnected: false,
        objects: []
    }

    componentDidMount() {
        this._isMounted = true;
        this.refresh();
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    componentDidUpdate(prevProps) {
        if (this.props.objectIds !== prevProps.objectIds) {
            this.refresh();
        }
    }

    refresh() {
        this.setState({
            waiting: true,
            disconnected: false,
            objects: []
        })
        this.fetchObjects();
    }

    render() {

        const { classes } = this.props;

        if (this.state.waiting) {
            return <div className={classes.root}>
                <CircularProgress className={classes.progress} />
            </div>;
        }

        else if (this.state.disconnected) {
            return <div className={classes.root}>
                <WarningPanel text="Verbindung zum Server unterbrochen!" />
            </div>;
        }

        else {
            return <div className={classes.root}>
                {
                    this.state.objects.length > 0 && <ObjectTable
                        items={this.state.objects}
                        onSelect={(index, object) => this.props.pushAndEditObject(object.id, false)}
                        onEdit={(index, object) => this.props.pushAndEditObject(object.id, false)}
                    />
                }
                {
                    this.state.objects.length < this.props.objectIds.length && <WarningPanel text="Wegen fehlender Berechtigungen können nicht alle Elemente angezeigt werden!" />
                }
            </div>;
        }
    }

    async fetchObjects() {
        const objectIds = this.props.objectIds;
        try {
            const json = await fetchJSON(
                REST_SERVER_BASE_URL + REST_CALL_OBJECT_QUERY.path, {
                    method: REST_CALL_OBJECT_QUERY.method,
                    body: JSON.stringify({"id": {"$in": objectIds}}),
                    headers: {
                        "Content-Type": "application/json",
                        "Authorization": "Bearer " + this.props.accessToken
                    }
                }
            );
            if (this._isMounted && objectIds === this.props.objectIds) {
                this.setState({
                    waiting: false,
                    disconnected: false,
                    objects: json.items
                });
                // otherwise this information is not important any more
                // (possibly meanwhile another query is running)
            }
        }
        catch (error) {
            if (this._isMounted && objectIds === this.props.objectIds) {
                this.setState({
                    waiting: false,
                    disconnected: true,
                    objects: []
                });
                // otherwise this information is not important any more
                // (possibly meanwhile another query is running)
            }
        }
    }
}

const styles = theme => ({
    root: {
        marginTop: 20,
        marginBottom: 20
    },
    progress: {
        display: "block",
        margin: "16px auto"
    }
});

ObjectReferencePanel.propTypes = {
    objectIds: PropTypes.array.isRequired
};

const mapStateToProps = (state, props) => {
    return {
        accessToken: state.authorization.accessToken
    };
}

export default connect(mapStateToProps, {pushAndEditObject})(withStyles(styles)(ObjectReferencePanel));